/**
 * ExportLogDetailSheet — Painel lateral de detalhes de uma exportação
 * -----------------------------------------------------------------------------
 * Sheet à direita (fullscreen no mobile) no mesmo padrão visual dos sheets do
 * módulo Indicação (header com gradiente + avatar, SectionLabel + InfoRow em
 * cards arredondados, ScrollArea no corpo e rodapé fixo).
 *
 * Mostra, em linguagem do usuário:
 *   • Resumo: data, módulo, formato, entrega, e-mail de destino e registros;
 *   • Filtros usados: traduzidos pela chave `_resumo` gravada na auditoria
 *     (describeParams da fonte). Logs antigos, sem `_resumo`, caem no
 *     dicionário de rótulos e valores abreviados — nunca JSON cru;
 *   • Ordenação e campos exportados;
 *   • Erro, quando a exportação falhou.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial (substitui o modal
 *          ExportLogDetailsDialog, seguindo o padrão dos sheets de Indicação).
 */

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  Calendar,
  FileDown,
  FileSpreadsheet,
  FileText,
  Mail,
  Download,
  Database,
  ListFilter,
  ArrowUpDown,
  Columns3,
  AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import { EXPORT_SOURCES } from '@/lib/export/exportRegistry';
import type { ExportLogRow } from '@/hooks/useExportLogs';
import type { ExportParamSummary } from '@/lib/export/types';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  registro: ExportLogRow | null;
}

// ============================================================================
// HELPERS VISUAIS (mesmo padrão dos sheets do módulo Indicação)
// ============================================================================

function SectionLabel({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </div>
  );
}

function InfoRow({
  label,
  value,
  icon: Icon,
  index,
}: {
  label: string;
  value: React.ReactNode;
  icon?: LucideIcon;
  /** Alterna o fundo para dar ritmo visual (padrão dos sheets) */
  index?: number;
}) {
  return (
    <div
      className={cn(
        'flex justify-between gap-3 px-3 py-2 text-sm rounded-md',
        index !== undefined && index % 2 === 0 && 'bg-muted/20',
      )}
    >
      <span className="text-xs text-muted-foreground shrink-0 self-center inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground/70" />}
        {label}
      </span>
      <span className="text-right text-sm font-medium break-words self-center">
        {value ?? '—'}
      </span>
    </div>
  );
}

/** Identificador curto no padrão do sistema (8 caracteres maiúsculos) */
function idCurto(id: string | null | undefined): string {
  return (id || '').slice(0, 8).toUpperCase();
}

/** Data/hora no padrão brasileiro */
function dataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// TRADUÇÃO DOS FILTROS (logs novos usam `_resumo`; antigos, dicionário)
// ============================================================================

/** Rótulos amigáveis das chaves crus gravadas em logs antigos */
const ROTULO_CHAVE: Record<string, string> = {
  kind: 'Origem',
  filters: null as unknown as string, // achatado em `achatarFiltros`
  sortField: 'Campo de ordenação',
  sortDirection: 'Sentido da ordenação',
  phaseName: 'Fase',
  search: 'Busca',
  status: 'Situação',
  priority: 'Prioridade',
};

/** Tradução de valores crus frequentes */
const ROTULO_VALOR: Record<string, string> = {
  list: 'Listagem "Todos os Negócios"',
  kanban: 'Coluna do Kanban',
  open: 'Em andamento',
  won: 'Ganho',
  lost: 'Perdido',
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
  asc: 'Mais antigos primeiro',
  desc: 'Mais recentes primeiro',
  created_at: 'Data de criação',
  updated_at: 'Última atualização',
  title: 'Título',
  value: 'Valor',
  expected_close_date: 'Previsão de fechamento',
  actual_close_date: 'Data de conclusão',
};

/** Converte um valor cru em texto amigável (usado só em logs antigos) */
function valorLegivel(valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '';
  if (Array.isArray(valor)) {
    return valor.map(valorLegivel).filter(Boolean).join(', ');
  }
  if (typeof valor === 'boolean') return valor ? 'Sim' : 'Não';
  if (typeof valor === 'object') return ''; // objetos crus não são exibidos
  const texto = String(valor);
  if (ROTULO_VALOR[texto]) return ROTULO_VALOR[texto];
  // Identificadores internos aparecem abreviados, nunca completos
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(texto)) {
    return idCurto(texto);
  }
  // Datas ISO longas viram dd/mm/aaaa
  if (/^\d{4}-\d{2}-\d{2}T/.test(texto)) {
    const d = new Date(texto);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('pt-BR');
  }
  return texto;
}

/** Achata o objeto `filters` dos logs antigos em linhas rotuladas */
function achatarFiltros(filtros: Record<string, unknown>): ExportParamSummary[] {
  const linhas: ExportParamSummary[] = [];
  for (const [chave, valor] of Object.entries(filtros)) {
    if (chave === '_resumo') continue;
    if (chave === 'filters' && valor && typeof valor === 'object' && !Array.isArray(valor)) {
      // Um nível abaixo: chaves do filtro da tela
      for (const [subChave, subValor] of Object.entries(valor as Record<string, unknown>)) {
        const texto = valorLegivel(subValor);
        if (!texto) continue;
        linhas.push({
          group: 'filtro',
          label: ROTULO_CHAVE[subChave] ?? subChave.replace(/_/g, ' '),
          value: texto,
        });
      }
      continue;
    }
    const texto = valorLegivel(valor);
    if (!texto) continue;
    const grupo = chave === 'sortField' || chave === 'sortDirection' ? 'ordenacao' : 'filtro';
    linhas.push({
      group: grupo,
      label: ROTULO_CHAVE[chave] ?? chave.replace(/_/g, ' '),
      value: texto,
    });
  }
  return linhas;
}

/** Extrai as linhas legíveis de filtros/ordenação do registro */
function linhasLegiveis(registro: ExportLogRow): {
  filtros: ExportParamSummary[];
  ordenacao: ExportParamSummary[];
} {
  const bruto = (registro.filters || {}) as Record<string, unknown>;
  const resumo = Array.isArray(bruto._resumo)
    ? (bruto._resumo as ExportParamSummary[]).filter((l) => l && l.label && l.value)
    : achatarFiltros(bruto);
  return {
    filtros: resumo.filter((l) => l.group === 'filtro'),
    ordenacao: resumo.filter((l) => l.group === 'ordenacao'),
  };
}

// ============================================================================
// COMPONENTE
// ============================================================================

export function ExportLogDetailSheet({ open, onOpenChange, registro }: Props) {
  if (!registro) return null;

  const fonte = EXPORT_SOURCES[registro.export_key];
  const modulo = fonte?.label ?? registro.export_key;
  const sucesso = registro.status === 'success';
  const entregaEmail = registro.delivery === 'email';

  const colunas = Array.isArray(registro.columns) ? (registro.columns as string[]) : [];
  const rotuloColuna = (chave: string) =>
    fonte?.columns.find((c) => c.key === chave)?.label ?? chave;

  const { filtros, ordenacao } = linhasLegiveis(registro);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[480px] sm:max-w-[480px] p-0 flex flex-col"
      >
        {/* Header com gradiente sutil + solicitante + situação */}
        <SheetHeader className="px-5 py-4 border-b bg-gradient-to-b from-muted/40 to-background">
          <div className="flex items-start gap-3">
            <SmartAvatar
              src={registro.user_avatar_url}
              name={registro.user_name}
              email={registro.user_email}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base leading-tight truncate">
                {registro.user_name || 'Usuário removido'}
              </SheetTitle>
              {registro.user_email && (
                <p className="text-xs text-muted-foreground truncate">{registro.user_email}</p>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Badge
                  variant={sucesso ? 'secondary' : 'destructive'}
                  className="font-normal"
                >
                  {sucesso ? 'Concluída' : 'Com erro'}
                </Badge>
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">
                  {idCurto(registro.id)}
                </span>
              </div>
            </div>
          </div>
        </SheetHeader>

        {/* Corpo rolável com blocos seccionados */}
        <ScrollArea className="flex-1">
          <div className="space-y-5 px-5 py-4">
            {/* ===== Resumo ===== */}
            <section className="space-y-2">
              <SectionLabel icon={FileDown}>Resumo</SectionLabel>
              <div className="rounded-xl border border-border/60 bg-card px-1 py-1">
                <InfoRow index={0} icon={Calendar} label="Data" value={dataHora(registro.created_at)} />
                <InfoRow index={1} icon={FileDown} label="Módulo" value={modulo} />
                <InfoRow
                  index={2}
                  icon={registro.format === 'xls' ? FileSpreadsheet : FileText}
                  label="Formato"
                  value={registro.format === 'xls' ? 'Planilha (XLSX)' : 'CSV'}
                />
                <InfoRow
                  index={3}
                  icon={entregaEmail ? Mail : Download}
                  label="Entrega"
                  value={entregaEmail ? 'Envio por e-mail' : 'Download'}
                />
                {entregaEmail && registro.recipient_email && (
                  <InfoRow index={4} icon={Mail} label="Enviado para" value={registro.recipient_email} />
                )}
                <InfoRow
                  index={entregaEmail && registro.recipient_email ? 5 : 4}
                  icon={Database}
                  label="Registros"
                  value={`${(registro.row_count ?? 0).toLocaleString('pt-BR')} registro(s)`}
                />
              </div>
            </section>

            {/* ===== Filtros usados ===== */}
            <section className="space-y-2">
              <SectionLabel icon={ListFilter}>Filtros usados</SectionLabel>
              <div className="rounded-xl border border-border/60 bg-card px-1 py-1">
                {filtros.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">
                    Nenhum filtro aplicado — exportação completa do recorte da tela.
                  </p>
                ) : (
                  filtros.map((linha, i) => (
                    <InfoRow key={`${linha.label}-${i}`} index={i} label={linha.label} value={linha.value} />
                  ))
                )}
              </div>
            </section>

            {/* ===== Ordenação ===== */}
            {ordenacao.length > 0 && (
              <section className="space-y-2">
                <SectionLabel icon={ArrowUpDown}>Ordenação</SectionLabel>
                <div className="rounded-xl border border-border/60 bg-card px-1 py-1">
                  {ordenacao.map((linha, i) => (
                    <InfoRow key={`${linha.label}-${i}`} index={i} label={linha.label} value={linha.value} />
                  ))}
                </div>
              </section>
            )}

            {/* ===== Campos exportados ===== */}
            <section className="space-y-2">
              <SectionLabel icon={Columns3}>
                Campos exportados{colunas.length ? ` (${colunas.length})` : ''}
              </SectionLabel>
              {colunas.length === 0 ? (
                <p className="text-sm text-muted-foreground">Não informado.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {colunas.map((chave) => (
                    <Badge key={chave} variant="secondary" className="font-normal">
                      {rotuloColuna(chave)}
                    </Badge>
                  ))}
                </div>
              )}
            </section>

            {/* ===== Erro ===== */}
            {registro.error_message && (
              <section className="space-y-2">
                <SectionLabel icon={AlertTriangle}>Erro</SectionLabel>
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2">
                  <p className="text-sm text-destructive break-words">{registro.error_message}</p>
                </div>
              </section>
            )}
          </div>
        </ScrollArea>

        {/* Rodapé fixo */}
        <div className="border-t px-5 py-3 flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
